import { siteConfig } from "@/lib/site-config";

// Tuscan-only gate. siteConfig is host-resolved per request, so on
// tuscancove.com this is true and the brand emits its own About copy
// unchanged; on non-Tuscan hosts it is false.
// The About route consumers (about/layout.tsx, about/page.tsx,
// about-content.tsx) only branch on isRoma, so a non-Roma, non-Tuscan
// host would otherwise fall through to the Tuscan exports below.
// Resolving these exports against siteConfig keeps Tuscan literals on the
// Tuscan host while every other host gets its own brand name/address/phone.
const isTuscan = siteConfig.name === "Tuscan Cove Bar & Patio";

// "+1-725-223-0714" -> "(725) 223-0714"
function formatPhoneDisplayFromConfig(rawPhone: string): string {
  const digits = rawPhone.replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return rawPhone;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

// "+1-725-223-0714" -> "tel:+17252230714"
function formatPhoneHrefFromConfig(rawPhone: string): string {
  const digits = rawPhone.replace(/\D/g, "");
  const e164 = digits.startsWith("1") ? digits : `1${digits}`;
  return `tel:+${e164}`;
}

const activeCityState = `${siteConfig.address.city}, ${siteConfig.address.state}`;
const activeCityStateZip = `${activeCityState} ${siteConfig.address.zip}`;

export type RestaurantLocation = {
  id: "southern-highlands";
  brandName: string;
  shortName: string;
  neighborhood: string;
  addressLine1: string;
  cityStateZip: string;
  phone: string;
  hours: string[];
  reservationUrl: string;
  pickupUrl: string;
  deliveryUrl: string;
};

export const tuscanCoveLocations: RestaurantLocation[] = [
  {
    id: "southern-highlands",
    brandName: "Tuscan Cove Bar & Patio",
    shortName: "Southern Highlands",
    neighborhood: "Southern Highlands",
    addressLine1: "12656 Southern Highlands Pkwy",
    cityStateZip: "Las Vegas, NV 89141",
    phone: "(725) 223-0714",
    hours: ["Mon-Sat 4pm-9pm", "Sun 10am-8pm", "Happy hour daily 4pm-6pm"],
    reservationUrl: siteConfig.reservationUrl,
    pickupUrl: siteConfig.orderUrl,
    deliveryUrl: siteConfig.orderUrl,
  },
];

export const tuscanCoveLinks = {
  foodMenu: "/menu",
  lunchMenu: "/menu#brunch-menu",
  drinkMenu: "/menu#drinks",
  events: "/events",
  catering: "/catering",
  parties: "/private-events",
  jobs: "/jobs",
  about: "/about",
  legacySpotSite: "https://tuscancove.com/",
  instagram: "https://www.instagram.com/tuscancove",
  facebook: "https://www.facebook.com/p/Tuscancove-61575968813643/",
  pinterest: "https://www.pinterest.com/tuscancove",
  linkedin: "https://www.linkedin.com/company/tuscan-cove/",
  x: "",
  yelp: siteConfig.yelpReservationUrl,
};

export function tuscanCoveAbsoluteUrl(path: string): string {
  const base = siteConfig.baseUrl.replace(/\/$/, "");
  if (path.startsWith("http")) return path;
  return base + (path.startsWith("/") ? path : "/" + path);
}

export const tuscanCoveHighlights = [
  "Southern Highlands neighborhood bar and patio",
  "Stone-fired pizza, smash burgers, salads, sandwiches, brunch, and cocktails",
  "Daily happy hour, group reservations, private parties, catering, and Sunday brunch",
];

export type ParsedMenuItem = {
  name: string;
  price: string;
  description?: string;
  image?: string;
  subcategory?: string;
};

export type ParsedMenuSection = {
  title: string;
  note?: string;
  items: ParsedMenuItem[];
};

export const tuscanCoveDinnerMenuSections: ParsedMenuSection[] = [
  {
    title: "Starters & Shareables",
    note: "Additional sauces +$1.00",
    items: [
      {
        name: "*Shrimp Cocktail",
        price: "$19.00",
        description: "Shrimp, cocktail sauce, lemon.",
        image: "/images/menu/tuscan-cove-shrimp-cocktail.jpg",
        subcategory: "Starters"
      },
      {
        name: "Garlic Cheese Bread",
        price: "$10.00",
        description: "Garlic, mozzarella, pomodoro.",
        image: "/images/menu/tuscan-cove-garlic-cheese-bread.jpg",
        subcategory: "Starters"
      },
      {
        name: "Cheese & Charcuterie",
        price: "$22.00",
        description: "Seasonal cheeses and meats.",
        image: "/images/menu/tuscan-cove-cheese-charcuterie.jpg",
        subcategory: "Starters"
      },
      {
        name: "Nachos",
        price: "$15.00",
        description: "Tortilla Chips, Cheese, Sour Cream, Pico De Gallo, Jalepenos",
        subcategory: "Starters"
      },
      {
        name: "Chicken Wings",
        price: "$15.00/1 lb",
        description: "Choose 2: Buffalo | BBQ | Blue Cheese | Ranch",
        image: "https://static.spotapps.co/spots/5b/e4f69f31e844cfa0ce36a3b4f1b579/full",
        subcategory: "Shareables"
      },
      {
        name: "Chicken Tenders",
        price: "$15.00",
        description: "Choose 2: Blue Cheese | Ranch | BBQ | Buffalo",
        image: "/images/menu/tuscan-cove-chicken-tenders.jpg",
        subcategory: "Shareables"
      },
      {
        name: "Basket of Fries",
        price: "$7.00",
        description: "Add: Garlic +$3.00 | Carne Asada +$10.00",
        image: "/images/menu/tuscan-cove-basket-of-fries.jpg",
        subcategory: "Shareables"
      }
    ]
  },
  {
    title: "Stone Fired Pizza",
    note: "Toppings +$2.50: Mushrooms, Onions, Tomatoes, Olives Bell Peppers, Jalapeno, Pepperoncini, Anchovies, Sausage, Prosciutto, Ham, Pepperoni, Bacon Salads",
    items: [
      {
        name: "Cheese",
        price: "10\" $14.00 / 18\" $20.00",
        description: "Mozzarella, pomodoro, oregano.",
        image: "https://static.spotapps.co/spots/20/991a33e56f4116b108f23c04998ed6/full"
      },
      {
        name: "Pepperoni",
        price: "18\" $22.00",
        description: "Mozzarella, pomodoro, pepperoni.",
        image: "/images/menu/tuscan-cove-pepperoni.png"
      },
      {
        name: "Supreme",
        price: "10\" $17.00 / 18\" $25.00",
        description: "Mozzarella, pomodoro, mushrooms, onion, olives, bell peppers, pepperoni, sausage.",
        image: "/images/menu/tuscan-cove-supreme.jpg"
      },
      {
        name: "Margarita",
        price: "10\" $15.00 / 18\" $22.00",
        description: "Pomodoro, mozzarella, basil, olive oil.",
        image: "/images/menu/tuscan-cove-margarita.jpg"
      },
      {
        name: "Buffalo Chicken",
        price: "10\" $15.00 / 18\" $22.00",
        description: "Mozzarella, Grilled Chicken, Onion, Buffalo Sauce",
        image: "/images/menu/tuscan-cove-buffalo-chicken.jpg"
      }
    ]
  },
  {
    title: "Sandwiches",
    items: [
      {
        name: "The Italian",
        price: "$15.00",
        description: "Salami, capicola, ham, lettuce, tomato, vinaigrette.",
        image: "/images/menu/tuscan-cove-italian-cold-cut-sub-studio.png"
      },
      {
        name: "The Pastrami",
        price: "$15.00",
        description: "Pastrami, mustard, rye."
      },
      {
        name: "Chicken Prosciutto Caprese",
        price: "$15.00",
        description: "Chicken, prosciutto, fresh mozzarella, basil, olive oil.",
        image: "/images/menu/tuscan-cove-chicken-caprese-panino-studio.png"
      },
      {
        name: "Chicken Parmesan",
        price: "$15.00",
        description: "Pomodoro, mozzarella, parmesan.",
        image: "https://static.spotapps.co/spots/c3/d37c64f6ca4b4ab666824d03b9fbcd/full"
      },
      {
        name: "Eggplant Parmesan",
        price: "$15.00",
        description: "Pomodoro, mozzarella, parmesan.",
        image: "/images/menu/tuscan-cove-eggplant-parmigiana-panino-studio.png"
      },
      {
        name: "Sausage & Peppers",
        price: "$15.00",
        description: "Sausage, peppers, provolone.",
        image: "/images/menu/tuscan-cove-sausage-peppers-panino-studio.png"
      },
      {
        name: "Steak, Mushroom & Pepper Panino",
        price: "$15.00",
        description: "Steak, sautéed mushrooms, peppers, provolone.",
        image: "/images/menu/tuscan-cove-steak-mushroom-pepper-panino-studio.png"
      }
    ]
  },
  {
    title: "Salads",
    note: "Add: Chicken $5.00 | Steak $10.00 | *Salmon (4 oz) $10.00 | Shrimp $10.00",
    items: [
      {
        name: "Tomato Salad",
        price: "$15.00",
        description: "Tomato, mozzarella, basil, olive oil. Add chicken +$5.00, steak +$10.00, salmon +$10.00, or shrimp +$10.00.",
        image: "/images/menu/tuscan-cove-tomato-salad.jpg"
      },
      {
        name: "Italian Chop",
        price: "$15.00",
        description: "Romaine, tomato, select meats, red onion. Add chicken +$5.00, steak +$10.00, salmon +$10.00, or shrimp +$10.00.",
        image: "https://static.spotapps.co/spots/a1/a760de196343b4869c5ecc4211f9b7/full"
      },
      {
        name: "Wedge Salad",
        price: "$15.00",
        description: "Iceberg, blue cheese, bacon, tomato, egg. Add chicken +$5.00, steak +$10.00, salmon +$10.00, or shrimp +$10.00.",
        image: "https://static.spotapps.co/spots/4e/d0eab308ca431bb361c9975beea854/full"
      },
      {
        name: "*Caesar Salad",
        price: "$15.00",
        description: "Romaine, crouton, parmesan. Add chicken +$5.00, steak +$10.00, salmon +$10.00, or shrimp +$10.00.",
        image: "/images/menu/tuscan-cove-caesar-salad.jpg"
      },
      {
        name: "Fiesta Salad",
        price: "$15.00",
        description: "Romaine, tortilla strips, tomato, red peppers, onion, radish, corn. Add chicken +$5.00, steak +$10.00, salmon +$10.00, or shrimp +$10.00.",
        image: "/images/menu/tuscan-cove-fiesta-salad.jpg"
      }
    ]
  },
  {
    title: "Mains",
    note: "Avocado | Grilled Onions | Mushrooms | Bacon +$3.00",
    items: [
      {
        name: "*Salmon Burger",
        price: "$20.00",
        description: "Tomato, avocado, lettuce, mayo, French fries.",
        image: "/images/menu/tuscan-cove-salmon-burger.jpg"
      },
      {
        name: "Fish & Chips",
        price: "$22.00",
        description: "Tartar sauce, French fries.",
        image: "/images/menu/tuscan-cove-fish-chips.jpg"
      },
      {
        name: "*Double Smash Burger",
        price: "$18.00",
        description: "Cheese, pickles, special sauce, French fries.",
        image: "https://static.spotapps.co/spots/5c/2973eb0bf546ceab2c1803a7d29a24/full"
      },
      {
        name: "*Carne Asada",
        price: "$26.00",
        description: "Corn tortillas, guacamole, pico de gallo, onions, bell peppers.",
        image: "https://static.spotapps.co/spots/36/edf2d1877348f58bc353cb67155c9d/full"
      },
      {
        name: "*Blackened Salmon",
        price: "$25.00",
        description: "Seasonal vegetables."
      },
      {
        name: "Grilled Shrimp Skewers",
        price: "$26.00",
        description: "Onions, bell peppers, corn tortillas, guacamole, pico de gallo.",
        image: "https://static.spotapps.co/spots/40/aa0502888c4b6e9cc848812a8452e2/full"
      }
    ]
  }
];

export const tuscanCoveBrunchMenuSections: ParsedMenuSection[] = [
  {
    title: "Sunday Brunch",
    note: "Available Sunday only.",
    items: [
      {
        name: "Steak & Eggs",
        price: "$18.00",
        description: "Skirt steak, sunnyside-up egg, breakfast potatoes.",
        subcategory: "Eggs & More"
      },
      {
        name: "All American Breakfast",
        price: "$12.00",
        description: "2 eggs, 2 bacon or sausage, breakfast potatoes, and toast.",
        subcategory: "Eggs & More"
      },
      {
        name: "Breakfast Burrito",
        price: "$12.00",
        description: "Eggs and chorizo or vegetables, black beans.",
        subcategory: "Eggs & More"
      },
      {
        name: "Breakfast Bowl",
        price: "$12.00",
        description: "Pastrami hash, sunnyside up egg, breakfast potatoes.",
        subcategory: "Eggs & More"
      },
      {
        name: "Vegetable Frittata with Arugula Salad",
        price: "$12.00",
        description: "Egg, cheese, and vegetables.",
        subcategory: "Eggs & More"
      },
      {
        name: "Sausage & Egg Muffin",
        price: "$12.00",
        description: "Egg, sausage patty, cheese.",
        subcategory: "Eggs & More"
      },
      {
        name: "Chilaquiles",
        price: "$12.00",
        description: "Eggs, tortillas, beans, sour cream, pico de gallo.",
        subcategory: "Eggs & More"
      },
      {
        name: "Avocado Toast",
        price: "$18.00",
        description: "Sourdough toast, sunnyside up egg, avocado, microgreens. Add Cotija +$2.00 | Smoked Salmon +$4.00",
        subcategory: "Savory"
      },
      {
        name: "The French Chicken",
        price: "$18.00",
        description: "Crispy fried chicken, French toast sticks, maple syrup, or hot honey.",
        subcategory: "Savory"
      },
      {
        name: "Smoked Salmon Bagel",
        price: "$15.00",
        description: "Cream cheese, smoked salmon, capers, red onion, and dill.",
        subcategory: "Savory"
      },
      {
        name: "Pastry Basket",
        price: "$12.00",
        description: "Fresh assortment.",
        subcategory: "Sweets"
      },
      {
        name: "Granola & Greek Yogurt",
        price: "$12.00",
        description: "Seasonal fruit, granola, and honey.",
        subcategory: "Sweets"
      },
      {
        name: "Buttermilk Pancakes (3)",
        price: "$12.00",
        description: "Add blueberry or chocolate +$2.00 each.",
        subcategory: "Sweets"
      },
      {
        name: "Potato Hash",
        price: "$3.00",
        subcategory: "Sides"
      },
      {
        name: "Beans",
        price: "$3.00",
        subcategory: "Sides"
      },
      {
        name: "Toast",
        price: "$3.00",
        subcategory: "Sides"
      },
      {
        name: "Bacon",
        price: "$6.00",
        subcategory: "Sides"
      },
      {
        name: "Egg",
        price: "$3.00",
        subcategory: "Sides"
      },
      {
        name: "Sausage",
        price: "$6.00",
        subcategory: "Sides"
      },
      {
        name: "Mimosas",
        price: "$24.00",
        description: "Classic orange or seasonal fruit.",
        subcategory: "Bottomless"
      },
      {
        name: "Bloody Mary",
        price: "$24.00",
        description: "Celery, bacon.",
        subcategory: "Bottomless"
      }
    ]
  }
];

export const tuscanCoveCateringMenuSections: ParsedMenuSection[] = [
  {
    title: "Catering & Party Platters",
    note: "2 One Topping Pizza's with either 12 Tenders or 16 Wings",
    items: [
      {
        name: "2 One Toppings Pizza with 16 Wings",
        price: "$55.00",
        description: "Choice of one topping for the Pizza's and Choice of Sauce for the Wings",
        subcategory: "Pizza Bundles"
      },
      {
        name: "2 One Toppings Pizza with 12 Chicken Tenders",
        price: "$55.00",
        description: "Choice of one topping for the Pizza's and Choice of Sauce for the Tenders",
        subcategory: "Pizza Bundles"
      },
      {
        name: "30 Tenders",
        price: "$60.00",
        description: "Hand Breaded Chicken Tenders",
        subcategory: "Party Platters"
      },
      {
        name: "40 WIngs",
        price: "$60.00",
        description: "Hand Breaded Chicken Wings",
        subcategory: "Party Platters"
      },
      {
        name: "Full Tray of Fries",
        price: "$40.00",
        subcategory: "Party Platters"
      },
      {
        name: "16 Piece Sandwich Platter",
        price: "$50.00",
        description: "Choose up to 4 of our signature sandwiches",
        subcategory: "Party Platters"
      },
      {
        name: "20 Beef Sliders",
        price: "$50.00",
        description: "Pickles, Secret Sauce, Cheddar Cheese",
        subcategory: "Party Platters"
      },
      {
        name: "Skirt Steak Fajita Platter",
        price: "$65.00",
        description: "Serves 6+, Onions, Peppers, Corn Tortillas, Avocado and Roasted Jalapeños",
        subcategory: "Party Platters"
      },
      {
        name: "Shrimp Fajita Platter",
        price: "$65.00",
        description: "Serves 6+, Onions, Peppers, Corn Tortillas, Avocado and Roasted Jalapeños",
        subcategory: "Party Platters"
      },
      {
        name: "Full Tray Salad",
        price: "$120.00",
        description: "Serves 20+",
        subcategory: "Party Platters"
      },
      {
        name: "Half Tray Salad",
        price: "$60.00",
        description: "Serves 10+",
        subcategory: "Party Platters"
      }
    ]
  }
];

export const tuscanCoveMenuSections = [
  ...tuscanCoveDinnerMenuSections,
  ...tuscanCoveBrunchMenuSections,
  ...tuscanCoveCateringMenuSections,
];

export const tuscanCoveAboutMetaDescription = isTuscan
  ? "Meet Tuscan Cove Bar & Patio, Southern Highlands' neighborhood sports bar and restaurant for food, drinks, brunch, happy hour, private events, and patio dining."
  : `Meet ${siteConfig.name}, the neighborhood restaurant and bar in ${activeCityState} for food, drinks, private events, and dining.`;

export const tuscanCoveAboutWebPageName = isTuscan
  ? "About Tuscan Cove Bar & Patio | Southern Highlands Las Vegas"
  : `About ${siteConfig.name} | ${activeCityState}`;

export type TuscanCoveAboutLocation = {
  id: "southern-highlands" | "henderson";
  publicName: string;
  addressLine1: string;
  cityStateZip: string;
  phoneDisplay: string;
  phoneHref: string;
  reservationUrl: string;
  hoursLines: string[];
};

export const tuscanCoveAboutLocations: TuscanCoveAboutLocation[] = isTuscan
  ? [
      {
        id: "southern-highlands",
        publicName: "Tuscan Cove Bar & Patio",
        addressLine1: "12656 Southern Highlands Pkwy",
        cityStateZip: "Las Vegas, NV 89141",
        phoneDisplay: "(725) 223-0714",
        phoneHref: "tel:+17252230714",
        reservationUrl: siteConfig.reservationUrl,
        hoursLines: tuscanCoveLocations[0]!.hours,
      },
    ]
  : [
      {
        id: "southern-highlands",
        publicName: siteConfig.name,
        addressLine1: siteConfig.address.line1,
        cityStateZip: activeCityStateZip,
        phoneDisplay: formatPhoneDisplayFromConfig(siteConfig.contact.phone),
        phoneHref: formatPhoneHrefFromConfig(siteConfig.contact.phone),
        reservationUrl: siteConfig.reservationUrl,
        hoursLines: tuscanCoveLocations[0]!.hours,
      },
    ];

const tuscanCoveAboutStoryTuscan = {
  heroDescription:
    "Tuscan Cove Bar & Patio is Southern Highlands' community sports bar and restaurant, where great food, cold drinks, game-day energy, and neighborhood hospitality come together.",
  legacy: {
    eyebrow: "Our story",
    titleBefore: "Built for the way Southern Highlands ",
    titleEmphasis: "actually gathers.",
    paragraphs: [
      "Located inside the Tuscan Highlands apartment complex at 12656 Southern Highlands Pkwy in Las Vegas, Tuscan Cove was created for the way locals spend time together: happy hour at the bar, dinner with family, brunch on the weekend, and nights spent watching the game with friends.",
      "The restaurant carries forward a longtime neighborhood tradition of patio dining and community connection in Southern Highlands. Before Tuscan Cove, this address was home to Becca's Restaurant & Lounge, a social restaurant known locally for its wine garden, firepit, expansive patio, and welcoming atmosphere.",
      "Today, that same spirit lives on in a new way: more energetic, more sports-focused, and built to feel like the neighborhood's go-to local hangout.",
    ],
  },
  quote: {
    text: "A neighborhood place for dinner, drinks, the game, brunch, and the people you come back with.",
    attribution: "Tuscan Cove Bar & Patio",
    attributionDetail: "Southern Highlands, Las Vegas",
  },
  stewardship: {
    eyebrow: "Hospitality roots",
    titleBefore: "Launched with local Las Vegas ",
    titleEmphasis: "hospitality experience.",
    paragraphs: [
      "Tuscan Cove Bar & Patio is owned and operated by Steven Kennedy of All In Hospitality in partnership with Executive Chef Ricardo Romo. Steven brings hospitality experience across restaurants, bars, lounges, and private-event spaces, and is involved day to day in service, the room, and the bar program.",
      "Chef Romo is a veteran Las Vegas chef whose career spans top kitchens on and off the Strip. He leads the Tuscan Cove kitchen, from stone-fired pizzas to Sunday brunch.",
      "That partnership shows up in the way Tuscan Cove is built: a chef-led kitchen, a strong bar, a roomy patio, and a neighborhood-first experience that works for dinner, drinks, game day, brunch, and celebrations.",
    ],
  },
  evolution: {
    eyebrow: "What makes it different",
    titleBefore: "A bar, a restaurant, and a patio with ",
    titleEmphasis: "real neighborhood range.",
    paragraphs: [
      "What makes Tuscan Cove special is the balance. It has the easygoing feel of a neighborhood bar, the comfort of a local restaurant, and a polished setting that works for casual dinners, celebrations, and group gatherings.",
      "Guests come in for handcrafted cocktails, wine, draft beer, stone-fired pizza, burgers, wings, shareable starters, Sunday brunch, and elevated comfort food that fits everything from a quick bite to a full night out.",
      "Sports are a big part of the experience. Big-screen viewing, bar specials, and a food-and-drinks lineup made for staying awhile make Tuscan Cove a natural place for football Sunday, playoff nights, and after-work meetups.",
    ],
  },
  hospitality: {
    eyebrow: "Patio & hospitality",
    titleBefore: "Indoor and outdoor spaces made for ",
    titleEmphasis: "easy connection.",
    paragraphs: [
      "The patio remains at the heart of it all. Indoor and outdoor spaces are designed for conversation, cocktails, relaxed dining, and the kind of night that can start with one drink and turn into dinner.",
      "It is a place for brunch in the sun, happy hour with coworkers, dinner with family, and celebrations that feel fun without feeling formal.",
      "At its core, Tuscan Cove is about community: a familiar Southern Highlands atmosphere guests can return to for dinner, drinks, the game, brunch, private events, or simply a good local night out.",
    ],
  },
} as const;

// Brand-neutral About story for any non-Tuscan host that falls through the
// consumers' isRoma-only gate. Carries no Tuscan-only facts
// (no "Tuscan Cove" literal, Southern Highlands neighborhood, the 12656
// Southern Highlands Pkwy / Becca's firepit address, stone-fired pizza, or
// Sunday brunch) and resolves the brand name and city from siteConfig.
const tuscanCoveAboutStoryActive = {
  heroDescription: `${siteConfig.name} is a neighborhood restaurant and bar in ${activeCityState}, where great food, drinks, and hospitality come together.`,
  legacy: {
    eyebrow: "Our story",
    titleBefore: `Built for the way ${activeCityState} `,
    titleEmphasis: "actually gathers.",
    paragraphs: [
      `Located at ${siteConfig.address.line1} in ${activeCityState}, ${siteConfig.name} was created for the way locals spend time together: drinks at the bar, dinner with family, and nights out with friends.`,
      `The restaurant carries forward a neighborhood tradition of dining and community connection in ${siteConfig.address.city}.`,
      "Today, that same spirit lives on in a welcoming room built to feel like the neighborhood's go-to local hangout.",
    ],
  },
  quote: {
    text: "A neighborhood place for dinner, drinks, and the people you come back with.",
    attribution: siteConfig.name,
    attributionDetail: activeCityState,
  },
  stewardship: {
    eyebrow: "Hospitality roots",
    titleBefore: "Launched with local hospitality ",
    titleEmphasis: "experience.",
    paragraphs: [
      `${siteConfig.name} is built around hospitality experience across restaurants, bars, and private-event spaces, with day-to-day focus on service, the room, and the bar program.`,
      "A chef-led kitchen leads the menu, balancing approachable favorites with dishes worth coming back for.",
      `That partnership shows up in the way ${siteConfig.name} is built: a chef-led kitchen, a strong bar, and a neighborhood-first experience for dinner, drinks, and celebrations.`,
    ],
  },
  evolution: {
    eyebrow: "What makes it different",
    titleBefore: "A bar and a restaurant with ",
    titleEmphasis: "real neighborhood range.",
    paragraphs: [
      `What makes ${siteConfig.name} special is the balance: the easygoing feel of a neighborhood bar, the comfort of a local restaurant, and a polished setting for casual dinners, celebrations, and group gatherings.`,
      "Guests come in for handcrafted cocktails, wine, beer, and elevated comfort food that fits everything from a quick bite to a full night out.",
      `Big-screen viewing, bar specials, and a food-and-drinks lineup made for staying awhile make ${siteConfig.name} a natural place for game day and after-work meetups.`,
    ],
  },
  hospitality: {
    eyebrow: "Hospitality",
    titleBefore: "Spaces made for ",
    titleEmphasis: "easy connection.",
    paragraphs: [
      "The room is designed for conversation, cocktails, relaxed dining, and the kind of night that can start with one drink and turn into dinner.",
      "It is a place for happy hour with coworkers, dinner with family, and celebrations that feel fun without feeling formal.",
      `At its core, ${siteConfig.name} is about community: a familiar ${activeCityState} atmosphere guests can return to for dinner, drinks, private events, or simply a good local night out.`,
    ],
  },
};

export const tuscanCoveAboutStory:
  | typeof tuscanCoveAboutStoryTuscan
  | typeof tuscanCoveAboutStoryActive = isTuscan
  ? tuscanCoveAboutStoryTuscan
  : tuscanCoveAboutStoryActive;

// DRAFT Roma About copy — review voice before launch.
export const romaKitchenAboutMetaDescription =
  "Roma Kitchen is the chef-led Italian restaurant from Chef Ricardo Romo, who left the Las Vegas Strip for intimate neighborhood rooms in Southern Highlands and Henderson.";

// DRAFT Roma About copy — review voice before launch.
export const romaKitchenAboutWebPageName =
  "About Chef's Roma Kitchen | Southern Highlands & Henderson";

// DRAFT Roma About copy — review voice before launch.
export const romaKitchenAboutLocations: TuscanCoveAboutLocation[] = [
  {
    id: "southern-highlands",
    publicName: "Roma Kitchen",
    addressLine1: "10420 S Decatur Blvd #100",
    cityStateZip: "Las Vegas, NV 89141",
    phoneDisplay: "(725) 745-2478",
    phoneHref: "tel:+17257452478",
    reservationUrl: "/reservations#roma-reservation-widget",
    hoursLines: ["Mon-Sat 4pm-9pm", "Sun 4pm-8pm"],
  },
  {
    id: "henderson",
    publicName: "Chef's Roma Kitchen",
    addressLine1: "1550 W Horizon Ridge Pkwy Ste P",
    cityStateZip: "Henderson, NV 89012",
    phoneDisplay: "(725) 240-6153",
    phoneHref: "tel:+17252406153",
    reservationUrl:
      "https://www.google.com/maps/reserve/v/dine/c/GYmLWTH3LNU",
    hoursLines: ["Dinner daily from 4pm"],
  },
];

// DRAFT Roma About copy — review voice before launch.
export const romaKitchenAboutStory = {
  heroDescription:
    "Roma Kitchen is the chef-led Italian restaurant from Chef Ricardo Romo, who traded the Las Vegas Strip for intimate neighborhood rooms of classic Italian comfort food and good wine in Southern Highlands and Henderson.",
  legacy: {
    eyebrow: "Our chef",
    titleBefore: "Thirty years of cooking, brought home to a ",
    titleEmphasis: "neighborhood table.",
    paragraphs: [
      "Roma Kitchen is led by Chef Ricardo Romo, an executive chef and kitchen expert with more than 30 years of culinary experience. He began his career on Martha's Vineyard before returning to Las Vegas, where he cooked in some of the city's most demanding kitchens.",
      "That resume runs through Michael Mina at MGM Grand, STK at the Cosmopolitan, Beer Park at Paris Las Vegas, Mastro's Ocean Club at Aria and Crystals, and Toca Madera at Aria and Crystals. Along the way he opened and created concepts in Santa Rosa and Paso Robles in California, in Washington DC, and in San Francisco.",
      "Roma Kitchen is where all of that experience comes to rest: classic Italian comfort food and good wine, served in a small, intimate setting built for the local community rather than the Strip.",
    ],
  },
  quote: {
    text: "I have worked on the Strip, which I loved, but I wanted to step away from the glitz and connect with the local community. I want to serve classic, Italian comfort food and good wine in a small, intimate setting.",
    attribution: "Chef Ricardo Romo",
    attributionDetail: "Executive Chef, Roma Kitchen",
  },
  stewardship: {
    eyebrow: "Hospitality roots",
    titleBefore: "A chef-led kitchen with two decades of ",
    titleEmphasis: "operating backing.",
    paragraphs: [
      "Chef Ricardo Romo's kitchen is backed by owner and operator Steven Kennedy, who operates both locations through his Las Vegas hospitality group, Element Hospitality, and has spent more than two decades shaping Las Vegas hospitality. A Boston native with a finance degree, Steven has called Las Vegas home for more than 25 years.",
      "He opened Chef's Roma Kitchen in Henderson in February 2023, a roughly 4,000 square foot neighborhood Italian restaurant focused on approachable hospitality and dining consistency, then opened its sister branch, Roma Kitchen in Southern Highlands, a roughly 3,000 square foot room, in August 2024.",
      "The result is a partnership of craft and operations: a chef-led kitchen running the menu, and an operator who keeps both locations consistent night after night, with online reservations, private dining for celebrations, and catering for offices and homes.",
    ],
  },
  evolution: {
    eyebrow: "What makes it different",
    titleBefore: "Classic Italian comfort food, cooked with ",
    titleEmphasis: "fine-dining hands.",
    paragraphs: [
      "What sets Roma Kitchen apart is the pairing of an intimate neighborhood room with a chef who spent 30 years in fine-dining kitchens. The cooking is classic and comforting, but the standards come straight off the Strip.",
      "Guests come in for Italian classics, pasta, charred mains, and a wine list built to sit alongside the food rather than upstage it. It is the kind of Italian dinner Chef Ricardo Romo left the glitz to cook.",
      "Both rooms run the same kitchen point of view, so a dinner at Chef's Roma Kitchen in Henderson and a dinner at Roma Kitchen in Southern Highlands feel like the same Roma family, the same chef, and the same good wine.",
    ],
  },
  hospitality: {
    eyebrow: "Private dining & catering",
    titleBefore: "An intimate room made for ",
    titleEmphasis: "the unhurried Italian dinner.",
    paragraphs: [
      "Roma Kitchen was built small and intimate on purpose, the kind of room where dinner takes its own pace and the chef can stay close to the food. Southern Highlands books online right here and Henderson through Google Reserve, so it is easy to book a quiet weeknight dinner or a weekend table for the family.",
      "Both rooms host private dining for rehearsal dinners, anniversaries, business dinners, birthdays, and small celebrations, and the kitchen handles catering requests for offices, homes, and hosted gatherings, with menus built off the same dishes guests order on a regular visit.",
      "At its core, Roma Kitchen is Chef Ricardo Romo's vision come to life: classic Italian comfort food, good wine, and a warm welcome in a small setting that belongs to the local community.",
    ],
  },
} as const;

export const tuscanCoveFaq = [
  {
    question: "Where is Tuscan Cove Bar & Patio located?",
    answer:
      "Tuscan Cove Bar & Patio is located inside the Tuscan Highlands apartment complex at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141 in Southern Highlands.",
  },
  {
    question: "Is Tuscan Cove in Southern Highlands?",
    answer:
      "Yes. Tuscan Cove Bar & Patio is a Southern Highlands restaurant and bar on Southern Highlands Parkway in southwest Las Vegas.",
  },
  {
    question: "How do I get directions to Tuscan Cove?",
    answer:
      "Use 12656 Southern Highlands Pkwy, Las Vegas, NV 89141 for navigation. For help before you visit, call (725) 223-0714.",
  },
  {
    question: "What kind of restaurant is Tuscan Cove?",
    answer:
      "Tuscan Cove is a neighborhood bar and patio with stone-fired pizza, burgers, wings, salads, sandwiches, cocktails, beer, wine, Sunday brunch, and daily happy hour.",
  },
  {
    question: "Is Tuscan Cove a good local restaurant for dinner or drinks?",
    answer:
      "Tuscan Cove is built for local dinner, drinks, patio seating, game nights, happy hour, brunch, and group gatherings in Southern Highlands.",
  },
  {
    question: "What is there to do at Tuscan Cove?",
    answer:
      "Guests come for dinner, cocktails, Sunday brunch, daily happy hour, sports on big screens, patio seating, events, private parties, and catering inquiries.",
  },
  {
    question: "Does Tuscan Cove have patio seating?",
    answer:
      "Yes. Tuscan Cove has a patio that works well for mild Las Vegas evenings, brunch, happy hour, and relaxed outdoor dining when weather allows.",
  },
  {
    question: "Can I order online from Tuscan Cove?",
    answer:
      "Yes. Tuscan Cove links online ordering from the website for current pickup and delivery availability.",
  },
  {
    question: "Does Tuscan Cove take reservations?",
    answer:
      "Yes. Reservations route through the Tuscan Cove booking page.",
  },
  {
    question: "Does Tuscan Cove offer catering and private parties?",
    answer:
      "Yes. Tuscan Cove accepts catering and group reservation inquiries for birthdays, corporate events, celebrations, and other gatherings.",
  },
  {
    question: "Does Tuscan Cove host events?",
    answer:
      "Yes. Tuscan Cove lists public events and supports group dining, private parties, catering, and special celebrations through the website.",
  },
  {
    question: "When is happy hour?",
    answer:
      "The public site lists happy hour daily from 4 PM to 6 PM at the bar.",
  },
  {
    question: "How do I contact Tuscan Cove?",
    answer:
      "Call (725) 223-0714, use the reservation and inquiry forms on the website, or email steven@tuscancove.com for catering and event questions.",
  },
  {
    question: "What is the Southern Highlands neighborhood?",
    answer:
      "Southern Highlands is a master-planned community in southwest Las Vegas, zip code 89141, situated south of the 215 beltway near the Nevada-California border.",
  },
  {
    question: "Where exactly is Southern Highlands located in Las Vegas?",
    answer:
      "Southern Highlands sits in the southwestern corner of the Las Vegas Valley, bounded by the 215 beltway to the north and stretching toward the Nevada-California border. Southern Highlands Pkwy is the neighborhood's main commercial corridor.",
  },
  {
    question: "What attractions are in the Southern Highlands area?",
    answer:
      "Southern Highlands offers the Southern Highlands Golf Club, scenic desert trails, community parks, and a walkable town center with dining and everyday services. Tuscan Cove Bar and Patio is a gathering spot for residents and visitors on Southern Highlands Pkwy.",
  },
  {
    question: "What outdoor activities can I do in Southern Highlands?",
    answer:
      "Residents and visitors enjoy hiking desert trails, cycling, and visiting community parks throughout Southern Highlands. The neighborhood's open spaces and mild winter climate make it a popular area for outdoor recreation.",
  },
  {
    question: "What is the climate like in Southern Highlands, Las Vegas?",
    answer:
      "Southern Highlands has a hot desert climate with summers regularly reaching above 100 degrees Fahrenheit from June through September, mild winters with daytime highs in the 50s and 60s, and very low annual rainfall.",
  },
  {
    question: "What are some of the best restaurants in Southern Highlands?",
    answer:
      "Tuscan Cove Bar and Patio is a well-known neighborhood restaurant on Southern Highlands Pkwy, serving stone-fired pizza, burgers, wings, cocktails, and brunch. The Southern Highlands town center has a variety of dining options within a short drive.",
  },
  {
    question: "How do I get to Southern Highlands from the Las Vegas Strip?",
    answer:
      "Southern Highlands is about 20 minutes south of the Las Vegas Strip via I-15 South. Take I-15 to the Southern Highlands Pkwy exit to reach the neighborhood's commercial center and dining options.",
  },
  {
    question: "How far is Southern Highlands from Harry Reid International Airport?",
    answer:
      "Southern Highlands is approximately 15 minutes from Harry Reid International Airport (formerly McCarran) via I-15 South, making it a convenient base for visitors who want to stay outside the Strip corridor.",
  },
  {
    question: "What is the history of the Southern Highlands community?",
    answer:
      "Southern Highlands was developed in the late 1990s and early 2000s as one of Las Vegas's largest master-planned communities. It was designed with a mix of residential neighborhoods, a private golf club, parks, and a commercial center serving the southwest valley.",
  },
  {
    question: "Is Southern Highlands a master-planned community?",
    answer:
      "Yes. Southern Highlands is a master-planned community in southwest Las Vegas with parks, a golf club, and neighborhood amenities maintained as part of the original community design.",
  },
  {
    question: "What golf is available in Southern Highlands?",
    answer:
      "The Southern Highlands Golf Club is a private, members-only club within the community, offering a championship course. After a round, many golfers stop at Tuscan Cove Bar and Patio on Southern Highlands Pkwy for happy hour, dinner, or cocktails.",
  },
  {
    question: "What parks are in Southern Highlands?",
    answer:
      "Southern Highlands has several community parks and open green spaces spread throughout the neighborhoods, maintained by the community association as part of the master-planned design.",
  },
  {
    question: "What is near Southern Highlands, Las Vegas?",
    answer:
      "The South Point Hotel and Casino is a prominent landmark just minutes from Southern Highlands. The 215 beltway provides quick access to the Strip, Harry Reid Airport, and other parts of the valley.",
  },
  {
    question: "Does Southern Highlands have good schools?",
    answer:
      "Southern Highlands is served by several public and private schools, including Southern Highlands Preparatory School within the community.",
  },
  {
    question: "What events are held in the Southern Highlands area?",
    answer:
      "Southern Highlands hosts seasonal gatherings and neighborhood meetups throughout the year.",
  },
  {
    question: "Is there parking near Tuscan Cove in Southern Highlands?",
    answer:
      "Yes. Tuscan Cove has on-site parking inside the Tuscan Highlands apartment complex at 12656 Southern Highlands Pkwy, making it an easy stop for dine-in, pickup, and events.",
  },
  {
    question: "What shopping is near Tuscan Cove in Southern Highlands?",
    answer:
      "The Southern Highlands Pkwy corridor has grocery stores, retail shops, and everyday services within walking distance or a short drive from Tuscan Cove Bar and Patio. It is a convenient neighborhood stop for dining before or after errands.",
  },
];

const homepageFaqQuestions = new Set([
  "Where is Tuscan Cove Bar & Patio located?",
  "How do I get directions to Tuscan Cove?",
  "Does Tuscan Cove take reservations?",
  "Can I order online from Tuscan Cove?",
  "When is happy hour?",
  "Does Tuscan Cove have patio seating?",
  "Does Tuscan Cove offer catering and private parties?",
  "How do I contact Tuscan Cove?",
]);

export const tuscanCoveHomepageFaq = tuscanCoveFaq.filter((item) =>
  homepageFaqQuestions.has(item.question)
);
